import {
  type Ref,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  Column,
  EmptyState,
  ExperimentIcon,
  FilterIcon,
  FrameStackIcon,
  IconButton,
  Row,
  SegmentedControl,
  SegmentedControlOption,
  Tooltip,
  TraitIcon,
} from "@hightouch/htui";
import { EditorView } from "@codemirror/view";
import orderBy from "lodash/orderBy";

import { Editor } from "src/components/editor";
import type { ColumnOption } from "src/formkit/components/formkit-context";
import { CantFindColumnMessage } from "src/formkit/components/mapper-v2/cant-find-column-message";
import { useMapperListbox } from "src/formkit/components/mapper-v2/hooks/use-mapper-listbox";
import {
  type MapperFocusTarget,
  MapperList,
  MapperListRow,
  MapperListSection,
  MapperListSkeleton,
  MapperPaneFooter,
  MapperRowIcon,
  MapperSearch,
} from "src/formkit/components/mapper-v2/mapper-list";
import type { MapperValue } from "src/formkit/components/mapper-v2/mapper-selection";
import {
  getTemplateColumnItems,
  insertTemplateFunction,
  insertTemplateVariable,
  type TemplateColumnItem,
} from "src/formkit/components/mapper-v2/template-input-utils";
import { useTemplatePreview } from "src/formkit/components/mapper-v2/template-preview-context";
import {
  getTemplateMetadataSections,
  type SyncVariableOption,
} from "src/formkit/components/mapper-v2/variable-options";
import type { TemplateInfo } from "src/formkit/components/types";
import { NEW_STATIC_ICONS } from "src/formkit/components/utils";
import { convertType, NEW_ICON_MAP } from "src/utils/destinations";
import { getSearchRegExp } from "src/utils/string";

type TemplateTab = "columns" | "metadata" | "filters";

const TEMPLATE_TABS: Array<{ value: TemplateTab; label: string }> = [
  { value: "columns", label: "Columns" },
  { value: "metadata", label: "Metadata" },
  { value: "filters", label: "Filters" },
];

// Long Liquid lines wrap rather than scroll sideways.
const EDITOR_EXTENSIONS = [EditorView.lineWrapping];

type TemplatePaneProps = {
  value: MapperValue;
  updateDraft: (update: (draft: MapperValue) => MapperValue) => void;
  onApply: () => void;
  /** The columns the Columns tab inserts. */
  columns: ColumnOption[];
  isLoadingColumns: boolean;
  isRealtimeSync: boolean;
  templates: TemplateInfo[];
  /** Takes a handle that focuses the Liquid editor, the pane's first control. */
  editorRef: Ref<MapperFocusTarget>;
};

type TemplateListItem =
  | { kind: "column"; key: string; column: TemplateColumnItem }
  | {
      kind: "metadata";
      key: string;
      section: string;
      option: SyncVariableOption;
    }
  | { kind: "filter"; key: string; filter: TemplateInfo };

/** A Liquid template, with columns, metadata, and filters to insert into it. */
export function TemplatePane({
  value,
  updateDraft,
  onApply,
  columns,
  isLoadingColumns,
  isRealtimeSync,
  templates,
  editorRef,
}: TemplatePaneProps) {
  const templatePreview = useTemplatePreview();
  const [tab, setTab] = useState<TemplateTab>("columns");
  const [search, setSearch] = useState("");
  const editorViewRef = useRef<EditorView | null>(null);
  const isEditorFocusPendingRef = useRef(false);

  // The Editor reports its view a render after it mounts, so a focus request that comes first waits for it.
  useImperativeHandle(
    editorRef,
    () => ({
      focus: () => {
        if (editorViewRef.current) editorViewRef.current.focus();
        else isEditorFocusPendingRef.current = true;
      },
    }),
    [],
  );

  const handleEditorInit = useCallback(({ view }: { view: EditorView }) => {
    editorViewRef.current = view;
    if (isEditorFocusPendingRef.current) {
      isEditorFocusPendingRef.current = false;
      view.focus();
    }
  }, []);

  const setTemplate = (template: string) =>
    updateDraft((draft) => ({ ...draft, template }));

  const trimmedSearch = search.trim();
  const regex = trimmedSearch ? getSearchRegExp(trimmedSearch, "i") : undefined;
  const matches = (text: string) => !regex || regex.test(text);

  const items: TemplateListItem[] =
    tab === "columns"
      ? getTemplateColumnItems(columns)
          .filter((column) => matches(column.label))
          .map((column) => ({
            kind: "column",
            key: `${column.kind}:${column.value}`,
            column,
          }))
      : tab === "metadata"
        ? getTemplateMetadataSections(isRealtimeSync).flatMap((section) =>
            section.options
              .filter((option) => matches(option.label))
              .map((option) => ({
                kind: "metadata" as const,
                key: option.liquid,
                section: section.label,
                option,
              })),
          )
        : orderBy(templates, "name")
            .filter((filter) => matches(filter.name))
            .map((filter) => ({
              kind: "filter",
              key: filter.name,
              filter,
            }));

  const insert = (item: TemplateListItem) => {
    updateDraft((draft) => ({
      ...draft,
      template:
        item.kind === "column"
          ? insertTemplateVariable(
              draft.template,
              item.column.value,
              item.column.kind,
            )
          : item.kind === "metadata"
            ? insertTemplateVariable(
                draft.template,
                item.option.liquid,
                "metadata",
              )
            : insertTemplateFunction(draft.template, item.filter),
    }));
  };

  const isLoadingList = tab === "columns" && isLoadingColumns;
  const listbox = useMapperListbox({
    optionCount: isLoadingList ? 0 : items.length,
    hasSearch: Boolean(trimmedSearch),
    onChoose: (index) => {
      const item = items[index];
      if (item) insert(item);
    },
  });

  const activeTab = TEMPLATE_TABS.find((option) => option.value === tab);
  const searchLabel = `Search ${activeTab?.label.toLowerCase() ?? ""}`;

  const renderItem = (item: TemplateListItem, index: number) => {
    const rowProps = {
      id: listbox.getOptionId(index),
      isActive: index === listbox.activeIndex,
      isSelected: false,
      onChoose: () => insert(item),
    };
    if (item.kind === "column") {
      return (
        <MapperListRow
          key={item.key}
          {...rowProps}
          description={item.column.inlineDescription}
          icon={<MapperRowIcon icon={getColumnItemIcon(item.column)} />}
          label={item.column.label}
        />
      );
    }
    if (item.kind === "metadata") {
      return (
        <MapperListRow
          key={item.key}
          {...rowProps}
          description={item.option.description}
          hasDescriptionBelow
          icon={<MapperRowIcon icon={NEW_STATIC_ICONS[item.option.type]} />}
          label={item.option.label}
        />
      );
    }
    return (
      <MapperListRow
        key={item.key}
        {...rowProps}
        description={item.filter.description}
        hasDescriptionBelow
        icon={<MapperRowIcon icon={FilterIcon} />}
        label={item.filter.name}
      />
    );
  };

  const renderItems = () => {
    if (tab !== "metadata") return items.map(renderItem);
    const sectionLabels = [
      ...new Set(
        items.flatMap((item) =>
          item.kind === "metadata" ? [item.section] : [],
        ),
      ),
    ];
    return sectionLabels.map((label) => (
      <MapperListSection key={label} label={label}>
        {items.map((item, index) =>
          item.kind === "metadata" && item.section === label
            ? renderItem(item, index)
            : null,
        )}
      </MapperListSection>
    ));
  };

  return (
    <>
      <Box flexShrink={0} p={4}>
        <Box
          borderColor="base.border"
          borderRadius="lg"
          borderStyle="solid"
          borderWidth="hairline"
          display="flex"
          height={40}
          overflow="hidden"
          sx={{
            // The editor pins its actions over the top-right corner, so lines wrap before them.
            "& .cm-content": { pr: templatePreview ? 8 : undefined },
          }}
        >
          <Editor
            actions={
              templatePreview ? (
                <Tooltip message="Test template">
                  <IconButton
                    aria-label="Test template"
                    icon={ExperimentIcon}
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      templatePreview.openPreview({
                        to: typeof value.to === "string" ? value.to : "",
                        template: value.template ?? "",
                        applyTemplate: setTemplate,
                      })
                    }
                  />
                </Tooltip>
              ) : undefined
            }
            bg="base.lightBackground"
            extensions={EDITOR_EXTENSIONS}
            language="liquid"
            minHeight="0px"
            placeholder="Select a column, metadata field, or filter below to add it to this Liquid template…"
            showLineNumbers={false}
            value={value.template ?? ""}
            onChange={setTemplate}
            onInit={handleEditorInit}
          />
        </Box>
      </Box>
      <Row alignItems="center" flexShrink={0} gap={4} px={4}>
        <SegmentedControl
          aria-label="Insert from"
          value={tab}
          onChange={(nextTab: TemplateTab) => {
            setTab(nextTab);
            setSearch("");
            listbox.resetActiveIndex();
          }}
        >
          {TEMPLATE_TABS.map((option) => (
            <SegmentedControlOption
              key={option.value}
              label={option.label}
              // The compact padding leaves the search box room for its placeholder.
              px="sm"
              value={option.value}
            />
          ))}
        </SegmentedControl>
        <Box flex={1} minWidth={0}>
          <MapperSearch
            label={searchLabel}
            listbox={listbox}
            padding={0}
            placeholder={`${searchLabel}…`}
            value={search}
            onChange={(nextSearch) => {
              setSearch(nextSearch);
              listbox.resetActiveIndex();
            }}
          />
        </Box>
      </Row>
      {isLoadingList ? (
        <MapperListSkeleton />
      ) : items.length > 0 ? (
        <MapperList
          activeOptionId={listbox.activeOptionId}
          label={activeTab?.label ?? ""}
          listId={listbox.listId}
          scrollRef={listbox.scrollRef}
        >
          {renderItems()}
        </MapperList>
      ) : (
        <Column flex={1} justifyContent="center" p={6}>
          <EmptyState
            message={tab === "columns" ? <CantFindColumnMessage /> : undefined}
            messageMaxW="100%"
            title={
              !trimmedSearch
                ? `No ${tab} available`
                : tab === "metadata"
                  ? "No metadata matches your search"
                  : `No ${tab} match your search`
            }
            variant="minimal"
          />
        </Column>
      )}
      <MapperPaneFooter>
        <Button variant="primary" onClick={onApply}>
          Apply template
        </Button>
      </MapperPaneFooter>
    </>
  );
}

function getColumnItemIcon(column: TemplateColumnItem) {
  if (column.kind === "trait") return TraitIcon;
  if (column.kind === "merged") return FrameStackIcon;
  return NEW_ICON_MAP[convertType(column.customType || column.type || "")];
}
